/**
 * What this product is made of, written down once.
 * ---------------------------------------------------------------------------
 * Three things read this file and they must not be able to disagree: the page
 * that draws the map (SiteContentPage), the manifest an agent fetches, and the
 * tools an agent calls (webmcp.js). So there is one list, here, and all three
 * are views of it.
 *
 * It is written by hand rather than derived from the router. A route table
 * says `/discovery` exists; it cannot say that the page answers "which
 * machines carry a critical finding", that the question is asked by opening
 * the lens, or that the answer is a link someone can send. That is the part
 * worth cataloguing, and only a person can write it.
 *
 * Honesty rule, and the whole reason the shape is what it is: a filter carries
 * a `param` ONLY where the page really reads it off the address today. The
 * rest are real filters that live in the page's own state - they are listed,
 * because someone looking for them wants to know they exist, but they are not
 * dressed up as links that would quietly do nothing.
 * ---------------------------------------------------------------------------
 */

/* The groups the map is read in - what someone is trying to do, not which
   component renders it. */
export const SECTIONS = [
  { id: "estate", label: "The estate", what: "What the scanner has found: machines, what runs on them, what is installed." },
  { id: "applications", label: "Applications", what: "The applications themselves - what they are, what they would cost to move, what was found on them." },
  { id: "forge", label: "Forge", what: "Runs against a codebase, and what they wrote." },
  { id: "delivery", label: "Delivery", what: "Designing a target and deploying to it." },
  { id: "operations", label: "Operations", what: "The estate at a glance, what happened, and what the scanner is doing." },
];

/* A severity is the same five words everywhere in the product. */
const SEVERITIES = ["critical", "high", "medium", "low", "unknown"];

/**
 * Every page someone can be sent to.
 *
 * - `path`     where it lives
 * - `rail`     the entry in the left-hand rail that goes there, if any
 * - `what`     one sentence: what question this page answers
 * - `also`     other words for it, for resolving what someone said out loud
 * - `needs`    the thing that has to be on for the page to exist at all
 * - `views`    the ways the same page can be drawn, each with the query that asks for it
 * - `filters`  what can be narrowed, each with the query that asks for it where
 *              the page reads one
 */
export const PAGES = [
  /* ── The estate ──────────────────────────────────────────────────────── */
  {
    id: "discovery",
    label: "Discovery",
    section: "estate",
    path: "/discovery",
    rail: "Discovery",
    what: "Every machine the scanner has seen, with what was found on it.",
    also: ["estate", "machines", "hosts", "servers", "discovered servers"],
    views: [
      { id: "table", label: "Table", what: "A row per machine.", query: {} },
      { id: "cards", label: "Cards", what: "A card per machine, on a stage that can be turned.", query: { view: "cards" } },
    ],
    filters: [
      { id: "search", label: "Search", kind: "text", param: "q", what: "Matches a name, an address, an operating system or installed software." },
      { id: "severity", label: "Severity", kind: "enum", param: "sev", values: [...SEVERITIES, "all"],
        what: "Opens the vulnerability lens on one severity, or on all of them.", needs: { view: "cards" } },
      { id: "only", label: "Keep only matches", kind: "flag", param: "only", value: "1",
        what: "Holds the stage to what matched instead of pointing at it." },
      { id: "grouped", label: "Group by package", kind: "flag",
        what: "Reads the findings column by the thing that gets fixed rather than one advisory at a time." },
      { id: "arrangement", label: "Arrangement", kind: "enum", values: ["grid", "sphere", "multi dim grid"],
        what: "How the cards stand. Keys 1, 2 and 3 on the stage." },
    ],
  },
  {
    id: "discovery-system",
    label: "Systems",
    section: "estate",
    path: "/discoverysystem",
    what: "The same estate read by system rather than by machine.",
    also: ["systems"],
    views: [{ id: "table", label: "Table", query: {} }],
    filters: [{ id: "search", label: "Search", kind: "text", what: "On the page's own field." }],
  },
  {
    id: "discovery-installed",
    label: "Installed software",
    section: "estate",
    path: "/discoveryinstalled",
    what: "Every package the scanner found installed, and what it is installed on.",
    also: ["installed software", "packages", "software"],
    views: [{ id: "table", label: "Table", query: {} }],
    filters: [{ id: "search", label: "Search", kind: "text", what: "On the page's own field." }],
  },

  /* ── Applications ────────────────────────────────────────────────────── */
  {
    id: "apps",
    label: "Applications",
    section: "applications",
    path: "/apps",
    rail: "App",
    what: "Every application the scanner found, with what is linked to it and what was found on it.",
    also: ["applications", "apps"],
    views: [
      { id: "table", label: "Table", what: "A row per application.", query: {} },
      { id: "cards", label: "Cards", what: "A card per application, on a stage.", query: { view: "cards" } },
    ],
    filters: [
      { id: "lens", label: "Lens", kind: "enum", param: "lens",
        values: ["vulnerable", "documented", "documentable"],
        what: "Only what can be fixed, only what has been written about, or only what Forge can run against.",
        needs: { view: "cards" } },
      { id: "severity", label: "Severity", kind: "enum", values: SEVERITIES.filter(k => k !== "unknown"),
        what: "With the vulnerability lens open, narrows to a severity." },
      { id: "search", label: "Search", kind: "text", what: "On the page's own field, over name, description, host and command line." },
      { id: "components", label: "Components only", kind: "flag", what: "The band's own switch: only application components." },
    ],
  },
  {
    id: "assess",
    label: "Cloud suitability",
    section: "applications",
    path: "/assess",
    what: "What an assessment made of each application: findings, lines, a score and what it recommends.",
    also: ["assessment", "assessed applications", "cloud suitability", "suitability"],
    views: [{ id: "table", label: "Table", query: {} }],
    filters: [{ id: "search", label: "Search", kind: "text", what: "On the page's own field." }],
  },
  {
    id: "graviton",
    label: "Graviton readiness",
    section: "applications",
    path: "/graviton",
    what: "What would run on Graviton, and what stands in the way.",
    also: ["graviton", "arm"],
    views: [{ id: "table", label: "Table", query: {} }],
    filters: [{ id: "search", label: "Search", kind: "text", what: "On the page's own field." }],
  },
  {
    id: "dotnet",
    label: ".NET modernization",
    section: "applications",
    path: "/dotnetmodernization",
    what: "The same reading for .NET: what can be brought forward, and what it would take.",
    also: ["dotnet", ".net", "net modernization"],
    views: [{ id: "table", label: "Table", query: {} }],
    filters: [{ id: "search", label: "Search", kind: "text", what: "On the page's own field." }],
  },
  {
    id: "traceability",
    label: "Traceability",
    section: "applications",
    path: "/traceability",
    what: "Where an application came from: the artifacts it was built from and the records that describe it.",
    also: ["traceability"],
    views: [
      { id: "artifacts", label: "Artifact repositories", path: "/traceability/artifact-repositories" },
      { id: "cmdb", label: "CMDB records", path: "/traceability/cmdb-records" },
    ],
    filters: [{ id: "search", label: "Search", kind: "text", what: "On the page's own field." }],
  },

  /* ── Forge ───────────────────────────────────────────────────────────── */
  {
    id: "forge",
    label: "Forge",
    section: "forge",
    path: "/forge",
    rail: "Forge",
    needs: "forge",
    what: "Every run Forge has made, and what it is making now.",
    also: ["forge", "runs", "forge runs"],
    views: [{ id: "board", label: "Board", query: {} }],
    filters: [
      { id: "search", label: "Search", kind: "text", what: "On the page's own field." },
      { id: "status", label: "Status", kind: "enum", values: ["running", "completed", "failed", "cancelled"],
        what: "The board's own filter." },
    ],
  },
  {
    id: "forge-wiki",
    label: "Wiki",
    section: "forge",
    path: "/forge/wiki",
    needs: "forge",
    what: "Every codebase Forge has documented, as a shelf of cards you can read from.",
    also: ["wiki", "documentation", "docs", "forge wiki"],
    views: [
      { id: "cards", label: "Cards", what: "The shelf.", query: {} },
      { id: "arrangement", label: "Grid · Sphere · Multi dim", what: "How the cards stand - keys 1, 2 and 3." },
    ],
    filters: [{ id: "search", label: "Search", kind: "text", what: "On the page's own field, over the codebase and its repository." }],
  },

  /* ── Delivery ────────────────────────────────────────────────────────── */
  {
    id: "designer",
    label: "Designer",
    section: "delivery",
    path: "/designer",
    rail: "Designer",
    needs: "deploy",
    what: "Where a target is drawn before anything is moved to it.",
    also: ["designer", "design"],
    views: [{ id: "canvas", label: "Canvas", query: {} }],
    filters: [],
  },
  {
    id: "deploy",
    label: "Deploy",
    section: "delivery",
    path: "/deploy",
    rail: "Deploy",
    needs: "deploy",
    what: "What has been deployed, and what is being deployed now.",
    also: ["deploy", "deployments"],
    views: [{ id: "list", label: "List", query: {} }],
    filters: [],
  },

  /* ── Operations ──────────────────────────────────────────────────────── */
  {
    id: "dashboard",
    label: "Dashboard",
    section: "operations",
    path: "/dashboard",
    rail: "Dashboard",
    what: "The estate at a glance, on boards you arrange yourself.",
    also: ["dashboard", "home", "overview", "boards"],
    views: [{ id: "boards", label: "Boards", query: {} }],
    filters: [{ id: "edit", label: "Edit mode", kind: "flag", what: "The band's own switch: arrange the boards." }],
  },
  {
    id: "security",
    label: "Security Blast",
    section: "operations",
    path: "/security",
    rail: "Security Blast",
    what: "One vulnerability, and everything in the estate it touches.",
    also: ["security", "security blast", "blast radius", "cve"],
    views: [{ id: "graph", label: "Graph", query: {} }],
    filters: [{ id: "search", label: "Search", kind: "text", what: "On the page's own field, over CVEs and packages." }],
  },
  {
    id: "multiverse",
    label: "Multiverse",
    section: "operations",
    path: "/multiverse",
    rail: "Multiverse",
    what: "The estate across every environment it exists in.",
    /* "introspection" stays an alias: it is what the page was called, what
       its route was, and what anything written against the old one still says
       when it means this. */
    also: ["multiverse", "introspection", "environments"],
    views: [{ id: "default", label: "Default", query: {} }],
    filters: [],
  },
  {
    id: "eventlog",
    label: "Events log",
    section: "operations",
    path: "/eventlog",
    rail: "Events log",
    what: "What the product has done, in the order it did it.",
    also: ["events", "event log", "events log", "log", "history"],
    views: [{ id: "table", label: "Table", query: {} }],
    filters: [{ id: "search", label: "Search", kind: "text", what: "On the page's own field." }],
  },
  {
    id: "scan",
    label: "Scan",
    section: "operations",
    path: "/scan",
    rail: "Scan",
    what: "The scanner itself: what it is looking at, and everything it is configured with.",
    also: ["scan", "scanner", "orchestrator", "proxy", "certificates"],
    views: [{ id: "scan", label: "Scan", query: {} }],
    filters: [
      { id: "tab", label: "Tab", kind: "enum", values: ["global", "proxy", "certs"],
        what: "The scanner's own settings, also reachable from Settings › Orchestrator." },
    ],
  },
  {
    id: "butler",
    label: "Butler",
    section: "operations",
    path: "/butler",
    rail: "Butler",
    what: "A small language model that runs on this machine, in this browser, and answers questions about the product.",
    also: ["butler", "assistant", "model", "ai", "chat"],
    views: [{ id: "chat", label: "Chat", query: {} }],
    filters: [
      { id: "model", label: "Model", kind: "enum",
        values: ["smollm2 360m", "qwen2.5 0.5b", "llama 3.2 1b", "gemma 2 2b"],
        what: "Which weights to fetch and keep. Chosen on the page; kept in this browser." },
    ],
  },
  {
    id: "content",
    label: "CNT",
    section: "operations",
    path: "/content",
    rail: "CNT",
    what: "This page: every page in the product, what it answers and what can be narrowed on it.",
    also: ["content", "site map", "sitemap", "index", "pages", "cnt"],
    views: [{ id: "map", label: "Map", query: {} }],
    filters: [{ id: "search", label: "Search", kind: "text", what: "Over page names, what they answer, and their filters." }],
  },
];

/* What has to be true for a page to be there at all. The caller knows: the
   rail already works these out for itself. */
export function sitePages({ forge = true, deploy = true } = {}) {
  return PAGES.filter(p => {
    if (p.needs === "forge") return !!forge;
    if (p.needs === "deploy") return !!deploy;
    return true;
  });
}

export function pageById(id) {
  return PAGES.find(p => p.id === id) || null;
}

/** A page by anything someone might call it - its id, its name, or a synonym. */
export function pageByName(said) {
  const q = String(said || "").trim().toLowerCase();
  if (!q) return null;
  return PAGES.find(p => p.id === q)
    || PAGES.find(p => p.label.toLowerCase() === q)
    || PAGES.find(p => (p.also || []).some(a => a === q))
    || null;
}

/**
 * Words left over after the page was named, read as filters.
 *
 * "Show critical machines" is one request, not two: the page is the estate and
 * `critical` is the lens it should open on. Only values the ADDRESS can carry
 * are read this way - anything else is left in the words, where it becomes a
 * search, which is what the page would have done with it anyway.
 */
function filtersIn(page, words) {
  const filters = {};
  let rest = words;
  (page.filters || []).forEach(f => {
    if (!f.param || f.kind !== "enum" || filters[f.id]) return;
    const hit = (f.values || []).find(v => WORD(rest, v));
    if (!hit) return;
    filters[f.id] = hit;
    rest = rest.replace(new RegExp(`(^|\\W)${hit}(\\W|$)`), " ").trim();
  });
  return { filters, rest };
}

/**
 * Where to go, as an address.
 *
 * Only what the page reads off the address is written into it - a filter the
 * page keeps in its own state is not a link, and pretending otherwise gives
 * someone a URL that silently does nothing. What could not be applied comes
 * back in `left`, so a caller can say so.
 */
export function hrefFor(page, { view, filters = {} } = {}) {
  if (!page) return { href: "/", left: [] };
  const chosen = view ? (page.views || []).find(v => v.id === view) : null;
  const base = (chosen && chosen.path) || page.path;
  const params = new URLSearchParams(chosen?.query || {});
  const left = [];

  Object.entries(filters).forEach(([id, value]) => {
    if (value === undefined || value === null || value === "" || value === false) return;
    const f = (page.filters || []).find(x => x.id === id);
    if (!f || !f.param) { left.push(id); return; }
    /* a filter that only means something in one view brings that view with it */
    if (f.needs?.view) {
      const v = (page.views || []).find(x => x.id === f.needs.view);
      Object.entries(v?.query || {}).forEach(([k, val]) => params.set(k, val));
    }
    params.set(f.param, f.kind === "flag" ? (f.value || "1") : String(value));
  });

  const q = params.toString();
  return { href: q ? `${base}?${q}` : base, left };
}

/** The whole thing as plain data - what the manifest and `list_pages` serve. */
export function siteMapJson({ forge = true, deploy = true } = {}) {
  return {
    name: "AppFactor",
    version: 1,
    generated: "from src/site/siteMap.js",
    note: "A filter with a `param` is reachable from the address. One without is applied on the page itself.",
    sections: SECTIONS,
    pages: sitePages({ forge, deploy }).map(p => ({
      id: p.id,
      label: p.label,
      section: p.section,
      path: p.path,
      rail: p.rail || null,
      what: p.what,
      also: p.also || [],
      views: (p.views || []).map(v => ({ id: v.id, label: v.label, what: v.what || "", path: v.path || p.path, query: v.query || {} })),
      filters: (p.filters || []).map(f => ({
        id: f.id,
        label: f.label,
        kind: f.kind,
        what: f.what || "",
        values: f.values || undefined,
        param: f.param || undefined,
        where: f.param ? "address" : "page",
      })),
    })),
  };
}

/* The verbs that turn something said out loud into a place to go. Without one
   of these, or an exact name, what was said is a search - which is what the
   page under it would have done with it. */
/* Longest first, always: "open" would otherwise match "open up discovery" and
   leave "up" behind as the thing to search for. */
const GOING = [
  "go to", "go into", "goto", "open up", "open", "show me", "show",
  "take me to", "take me", "navigate to", "navigate", "jump to", "switch to",
  "bring up", "visit",
  /* The way people actually ask for a page they know exists. These were going
     to the model, which guessed - "where is the wiki" came back as the
     dashboard - when the words after them are an exact page name the resolver
     can match with no thinking at all. */
  "where is", "where are", "where do i see", "where can i see", "where do i find",
  "where's", "find me", "i want to see", "i need to see", "let me see",
].sort((a, b) => b.length - a.length);

/* Whole words only. "alarm" contains "arm" and "planet" contains "net", and
   neither is a request for Graviton or for .NET. */
const WORD = (hay, needle) => {
  const safe = String(needle).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(^|\\W)${safe}(\\W|$)`).test(hay);
};

/**
 * What someone asked for, as a place - or nothing, which means it was not a
 * navigation at all and the words belong in the page's own search.
 *
 * Deliberately strict: "forge" typed into the applications search is a search
 * for the word forge, not a request to leave the page. A verb, or the whole
 * sentence being a page's name, is what makes it a journey.
 */
export function resolveAsk(said) {
  const raw = String(said || "").trim().toLowerCase().replace(/[.?!]+$/, "");
  if (!raw) return null;

  const exact = pageByName(raw);
  if (exact) return { page: exact, rest: "", filters: {} };

  const verb = GOING.find(v => raw.startsWith(`${v} `));
  if (!verb) return null;

  const rest = raw.slice(verb.length + 1).replace(/^(the|my|a)\s+/, "").trim();
  const page = pageByName(rest)
    /* "open the forge wiki page" - the noun is in there with a word after it */
    || pageByName(rest.replace(/\s+(page|screen|view|tab)$/, ""))
    || PAGES.find(p => (p.also || []).some(a => WORD(rest, a)))
    || PAGES.find(p => WORD(rest, p.label.toLowerCase()));
  if (!page) return null;

  /* what it was called, taken back out: the longest of its names that is in
     there, so "forge wiki" wins over "wiki" */
  const name = (page.also || []).concat([page.label.toLowerCase(), page.id])
    .filter(a => WORD(rest, a))
    .sort((a, b) => b.length - a.length)[0] || "";
  const words = rest
    .replace(name, " ")
    .replace(/\b(page|screen|view|tab|please)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  /* ...and of what is left, the part the address can carry is a filter, the
     rest is a search */
  const { filters, rest: leftover } = filtersIn(page, words);
  return { page, rest: leftover, filters };
}

export default PAGES;
